import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { formatIstDateTime, formatTimeAgoWords } from '../../utils/localDate.js';

const TONE = {
  requested: { dot: 'bg-slate-400', chip: 'bg-slate-100 text-slate-700' },
  pending: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800' },
  approved: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800' },
  rejected: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-800' },
  paid: { dot: 'bg-sky-500', chip: 'bg-sky-50 text-sky-800' },
};

function statusLabel(status) {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function TimelineItem({ tone, label, name, at, note, last }) {
  const colors = TONE[tone] || TONE.requested;
  const relative = at ? formatTimeAgoWords(at) : '';
  const exact = at ? formatIstDateTime(at) : '';
  return (
    <li className="relative flex gap-3 sm:gap-4">
      {!last && (
        <span className="absolute left-[7px] top-4 bottom-0 w-px bg-gray-200" aria-hidden="true" />
      )}
      <span className={`relative z-[1] mt-1.5 h-4 w-4 shrink-0 rounded-full ring-4 ring-white ${colors.dot}`} />
      <div className={`min-w-0 flex-1 ${last ? '' : 'pb-5'}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <p className="mt-0.5 text-sm sm:text-base font-semibold text-gray-900 break-words">{name}</p>
        {(relative || exact) && (
          <p className="mt-0.5 text-xs sm:text-sm text-gray-500">
            {relative}
            {exact ? <span className="text-gray-400"> · {exact}</span> : null}
          </p>
        )}
        {note ? (
          <p className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap break-words">
            {note}
          </p>
        ) : null}
      </div>
    </li>
  );
}

export function ExpenseStatusPanel({ expense }) {
  const events = Array.isArray(expense?.status_events) ? expense.status_events : [];
  const decisions = events.filter((event) => event.to_status === 'approved' || event.to_status === 'rejected');
  const fallbackDecision = !decisions.length && (expense?.status === 'rejected' || expense?.status === 'approved' || expense?.status === 'paid')
    ? {
      tone: expense.status === 'rejected' ? 'rejected' : 'approved',
      label: expense.status === 'rejected' ? 'Rejected' : 'Approved',
      name: expense.status === 'rejected'
        ? (expense.rejected_by_name || expense.rejected_by)
        : (expense.approved_by_name || expense.approved_by),
      at: expense.status === 'rejected' ? expense.rejected_at : expense.approved_at,
      note: expense.status_note,
    }
    : null;
  const showPaid = expense?.status === 'paid';
  const steps = [
    {
      key: 'requested',
      tone: 'requested',
      label: 'Requested',
      name: expense?.created_by_name || expense?.created_by || 'Unknown',
      at: expense?.created_at,
    },
    ...decisions.map((event) => ({
      key: `event-${event.id}`,
      tone: event.to_status,
      label: event.to_status === 'approved' ? 'Approved' : 'Rejected',
      name: event.acted_by_name || event.acted_by || 'Unknown',
      at: event.acted_at,
      note: event.note,
    })),
    ...(fallbackDecision?.name ? [{ key: 'decision', ...fallbackDecision }] : []),
    ...(showPaid ? [{
      key: 'paid',
      tone: 'paid',
      label: 'Paid',
      name: expense.paid_by || 'Recorded as paid',
      at: expense.payment_date,
    }] : []),
  ];

  return (
    <div>
      <ol className="mt-1">
        {steps.map((step, index) => (
          <TimelineItem
            key={step.key}
            tone={step.tone}
            label={step.label}
            name={step.name}
            at={step.at}
            note={step.note}
            last={index === steps.length - 1 && expense?.status !== 'pending'}
          />
        ))}
      </ol>
      {expense?.status === 'pending' && (
        <p className="mt-1 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Waiting for admin or accountant review.
        </p>
      )}
    </div>
  );
}

export default function ExpenseStatusModal({
  open,
  title,
  expense,
  mode,
  saving,
  onClose,
  onSubmit,
}) {
  const [status, setStatus] = useState('approved');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setStatus('approved');
    setNote('');
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const tone = TONE[expense?.status] || TONE.pending;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6">
      <button type="button" className="absolute inset-0 bg-slate-900/45" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex w-full max-h-[92vh] flex-col overflow-hidden bg-white shadow-2xl rounded-t-2xl sm:max-w-md sm:rounded-2xl md:max-w-lg"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5 sm:py-4">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h2>
            {expense ? (
              <p className="mt-0.5 text-xs sm:text-sm text-gray-500 break-all">{expense.expense_id}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
          {expense ? (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 sm:px-4">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Amount</p>
                <p className="text-lg sm:text-xl font-semibold text-gray-900 tabular-nums">
                  ₹{Number(expense.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${tone.chip}`}>
                {expense.status_display || statusLabel(expense.status)}
              </span>
            </div>
          ) : null}

          <ExpenseStatusPanel expense={expense} />

          {mode === 'review' && (
            <form
              className="mt-5 space-y-3 border-t border-gray-100 pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                onSubmit({ status, note: note.trim() });
              }}
            >
              <label className="block text-sm font-medium text-gray-700">
                Update status
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                >
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Note
                <textarea
                  required
                  rows={3}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder={status === 'approved' ? 'Why this is approved' : 'Why this is rejected'}
                  className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
                />
              </label>
              <button
                type="submit"
                disabled={saving || !note.trim()}
                className="w-full rounded-xl bg-teal-700 text-white py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save status'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
